//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';
import { motion, LayoutGroup } from 'framer-motion';

interface RCSegmentedControlProps {
  /** Should be `<RCSegmentedControl.Button />` components only. */
  children: React.ReactElement<ButtonProps>[];

  /** Standard HTML attribute – a unique ID to identify the group of buttons. (Used by the animation library.) */
  id: string;

  /** Render at full page width. (Defaults to width of contents.) */
  fullWidth?: boolean;

  /** Use during content reloads  */
  loading?: boolean;

  /**
   * Used to describe the element to screen readers.
   * Note: All controls should include either a label or be labelled by another element
  */
  'aria-label'?: string;

  /** Optionally describe this component using the id of a text element on the page */
  'aria-labelledby'?: string;
}

export function RCSegmentedControl({
  children,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  fullWidth = false,
  loading = false,
}: RCSegmentedControlProps) {

  return (
    <div
      className={classNames(
        'rc-segmented-control',
        { 'rc-segmented-control--full-width': fullWidth },
        { 'rc-segmented-control--loading': loading },
      )}
      role="group"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      <LayoutGroup id={id}>
        {children}
      </LayoutGroup>
    </div>
  );
}

interface ButtonProps {
  /** Button contents. Generally text or an RCIcon. */
  children: React.ReactNode;

  /** Callback function when this button is clicked */
  onClick: () => void;

  /** Optional prop to be used when displaying only an RCIcon. Slightly increases icon size */
  iconOnly?: boolean;

  /** Identifies this as the current item, both visually and to screen readers */
  selected?: boolean,
}

function Button({
  onClick,
  children,
  iconOnly = false,
  selected = false,
}: ButtonProps) {
  // Loading a motion.div server side breaks everything, so this defers loading it until the next render cycle
  const [loaded, setIsLoaded] = React.useState(false);
  React.useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <button
      type="button"
      className={classNames(
        'rc-segmented-control__button',
        { 'rc-segmented-control__button--icon-only': iconOnly },
        { 'rc-segmented-control__button--selected': selected },
      )}
      onClick={onClick}
      aria-current={selected}
    >
      {selected && loaded &&
        <motion.div
          aria-hidden="true"
          className="rc-segmented-control__button__highlight"
          layoutId="highlight"
        />
      }
      <span className="rc-segmented-control__button__content">
        {children}
      </span>
    </button>
  );
}

RCSegmentedControl.Button = Button;
